/**
 * Case Study Search
 * 
 * Handles live search for case studies and case study industries
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    
    const searchInput = document.getElementById('case-study-search-input');
    const searchClear = document.getElementById('case-study-search-clear');
    const searchResults = document.getElementById('case-study-search-results');
    const searchResultsContent = document.getElementById('case-study-search-results-content');
    const searchLoading = document.querySelector('.case-study-search-loading');

    if (!searchInput || !searchResults) {
      return; // Not on a page with case study search
    }
    
    let searchTimeout = null;
    let abortController = null;

    // Handle search input
    searchInput.addEventListener('input', function(e) {
      const searchTerm = e.target.value.trim();
      
      // Show/hide clear button
      if (searchClear) {
        searchClear.style.display = searchTerm ? 'flex' : 'none';
      }

      // Clear existing timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      
      // Cancel any pending request
      if (abortController) {
        abortController.abort();
      }

      // Hide results if search is too short
      if (searchTerm.length < 2) {
        searchResults.style.display = 'none';
        return;
      }

      // Show loading state
      searchResults.style.display = 'block';
      if (searchLoading) {
        searchLoading.style.display = 'block';
      }
      searchResultsContent.innerHTML = '';

      // Debounce search - wait 300ms after user stops typing
      searchTimeout = setTimeout(function() {
        performSearch(searchTerm);
      }, 300);
    });

    // Handle clear button
    if (searchClear) {
      searchClear.addEventListener('click', function() {
        searchInput.value = '';
        searchInput.focus();
        searchClear.style.display = 'none';
        searchResults.style.display = 'none';
      });
    }

    // Close search results when clicking outside
    document.addEventListener('click', function(e) {
      if (!e.target.closest('.case-study-search-container')) {
        searchResults.style.display = 'none';
      }
    });

    // Reopen results when focusing on input with content
    searchInput.addEventListener('focus', function() {
      if (searchInput.value.trim().length > 0 && searchResultsContent.innerHTML) {
        searchResults.style.display = 'block';
      }
    });

    /**
     * Perform the AJAX search. isRetry: already retried once with a fresh
     * nonce, so don't loop.
     */
    function performSearch(searchTerm, isRetry) {
      
      // Check if pcm_ajax is available
      if (typeof pcm_ajax === 'undefined') {
        console.error('Case Study Search: pcm_ajax is not defined');
        return;
      }
      
      // Create new abort controller for this request
      abortController = new AbortController();

      const formData = new FormData();
      formData.append('action', 'pcm_search_case_studies');
      formData.append('search', searchTerm);
      formData.append('nonce', pcm_ajax.case_studies_search_nonce);

      fetch(pcm_ajax.ajax_url, {
        method: 'POST',
        body: formData,
        signal: abortController.signal
      })
      .then(response => {
        return response.json();
      })
      .then(data => {
        // The page's nonce can expire before the cached page does: get a live
        // one and retry once.
        if (!data.success && !isRetry && data.data && data.data.message === 'Invalid nonce' && window.pcmFetchFreshNonce) {
          return window.pcmFetchFreshNonce('case_studies_search_nonce').then(nonce => {
            pcm_ajax.case_studies_search_nonce = nonce;
            performSearch(searchTerm, true);
          });
        }

        if (searchLoading) {
          searchLoading.style.display = 'none';
        }
        
        // Ensure results container is visible
        searchResults.style.display = 'block';

        if (data.success) {
          renderResults(data.data);
        } else {
          console.error('Case Study Search: Error response:', data);
          searchResultsContent.innerHTML = '<div class="case-study-search-error">Search failed. Please try again.</div>';
        }
      })
      .catch(error => {
        // Ignore abort errors (user is still typing)
        if (error.name === 'AbortError') {
          return;
        }
        
        console.error('Case Study Search: Fetch error:', error);
        if (searchLoading) {
          searchLoading.style.display = 'none';
        }
        searchResultsContent.innerHTML = '<div class="case-study-search-error">An error occurred. Please try again.</div>';
      });
    }

    /**
     * Render search results
     */
    function renderResults(data) {
      
      const caseStudies = data.case_studies || [];
      const industries = data.industries || [];
      
      if (caseStudies.length === 0 && industries.length === 0) {
        searchResultsContent.innerHTML = '<div class="case-study-search-no-results" style="padding: 1.5rem; text-align: center; color: #6c757d;">No results found</div>';
        return;
      }

      let html = '';

      // Render industries section
      if (industries.length > 0) {
        html += '<div class="case-study-search-section">';
        html += '<h3 class="case-study-search-section-title">Industries</h3>';
        html += '<ul class="case-study-search-list">';
        
        industries.forEach(function(industry) {
          html += '<li class="case-study-search-item case-study-search-industry">';
          html += '<a href="' + escapeHtml(industry.url) + '" class="case-study-search-link">';
          html += '<span class="case-study-search-name">' + escapeHtml(industry.name) + '</span>';
          html += '<span class="case-study-search-count">(' + industry.count + ' case ' + (industry.count === 1 ? 'study' : 'studies') + ')</span>';
          html += '</a>';
          html += '</li>';
        });
        
        html += '</ul>';
        html += '</div>';
      }

      // Render case studies section
      if (caseStudies.length > 0) {
        html += '<div class="case-study-search-section">';
        html += '<h3 class="case-study-search-section-title">Case Studies</h3>';
        html += '<ul class="case-study-search-list">';
        
        caseStudies.forEach(function(caseStudy) {
          html += '<li class="case-study-search-item case-study-search-case-study">';
          html += '<a href="' + escapeHtml(caseStudy.url) + '" class="case-study-search-link">';
          html += '<div class="case-study-search-name">' + escapeHtml(caseStudy.title) + '</div>';
          if (caseStudy.excerpt) {
            html += '<div class="case-study-search-excerpt">' + escapeHtml(caseStudy.excerpt) + '</div>';
          }
          html += '</a>';
          html += '</li>';
        });
        
        html += '</ul>';
        html += '</div>';
      }

      searchResultsContent.innerHTML = html;
    }

    /**
     * Escape HTML to prevent XSS
     */
    function escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

})();
